import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Pencil, Trash2, Shield, Plus, UserPlus, Store as StoreIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const Users = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    assigned_stores: []
  });

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchUsers();
      fetchStores();
    } else {
      toast.error('Access denied. Admin only.');
    }
  }, []);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stores`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (error) {
      console.error('Failed to load stores');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUserData.name || !newUserData.email || !newUserData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (newUserData.password.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUserData)
      });

      if (response.ok) {
        toast.success('User created successfully');
        setCreateDialogOpen(false);
        setNewUserData({
          name: '',
          email: '',
          password: '',
          role: 'staff',
          assigned_stores: []
        });
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success('User role updated');
        fetchUsers();
      } else {
        toast.error('Failed to update user role');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const openEditStoresDialog = (user) => {
    setEditingUser(user);
    setSelectedStores(user.assigned_stores || []);
    setDialogOpen(true);
  };

  const handleSaveStores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assigned_stores: selectedStores })
      });

      if (response.ok) {
        toast.success('Store access updated');
        setDialogOpen(false);
        fetchUsers();
      } else {
        toast.error('Failed to update store access');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const toggleStore = (storeId) => {
    if (selectedStores.includes(storeId)) {
      setSelectedStores(selectedStores.filter(id => id !== storeId));
    } else {
      setSelectedStores([...selectedStores, storeId]);
    }
  };

  const toggleNewUserStore = (storeId) => {
    const current = newUserData.assigned_stores || [];
    if (current.includes(storeId)) {
      setNewUserData({ ...newUserData, assigned_stores: current.filter(id => id !== storeId) });
    } else {
      setNewUserData({ ...newUserData, assigned_stores: [...current, storeId] });
    }
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store ? store.name : storeId;
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('User deleted');
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'manager':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'staff':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div className="text-center p-8">
        <Shield className="mx-auto mb-4 text-destructive" size={48} />
        <h2 className="font-heading font-bold text-2xl mb-2">Access Denied</h2>
        <p className="text-muted-foreground font-body">This page is only accessible to administrators.</p>
      </div>
    );
  }

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight" data-testid="users-title">Users</h1>
          <p className="text-muted-foreground font-body mt-2">Manage user accounts and permissions</p>
        </div>
        
        {/* Add User Button */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-user-btn">
              <UserPlus size={18} strokeWidth={1.5} className="mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="create-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-2xl">Create New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Name *</Label>
                <Input
                  value={newUserData.name}
                  onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  className="mt-1.5"
                  data-testid="new-user-name"
                />
              </div>

              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Email *</Label>
                <Input
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                  className="mt-1.5"
                  data-testid="new-user-email"
                />
              </div>

              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Password *</Label>
                <Input
                  type="password"
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  className="mt-1.5"
                  data-testid="new-user-password"
                />
                <p className="text-xs text-muted-foreground mt-1">Minimum 4 characters</p>
              </div>

              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Role</Label>
                <select
                  value={newUserData.role}
                  onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1.5"
                  data-testid="new-user-role"
                >
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Store Access (only show if not admin) */}
              {newUserData.role !== 'admin' && (
                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest mb-2 block">Store Access</Label>
                  <div className="border border-border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                    {stores.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No stores available</p>
                    ) : (
                      stores.map(store => (
                        <label 
                          key={store.id} 
                          className="flex items-center gap-2 p-2 hover:bg-secondary rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={(newUserData.assigned_stores || []).includes(store.id)}
                            onChange={() => toggleNewUserStore(store.id)}
                            className="w-4 h-4"
                            data-testid={`new-user-store-${store.id}`}
                          />
                          <StoreIcon size={14} className="text-muted-foreground" />
                          <span className="text-sm">{store.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                  {(newUserData.assigned_stores || []).length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {(newUserData.assigned_stores || []).length} store(s) selected
                    </p>
                  )}
                </div>
              )}

              {newUserData.role === 'admin' && (
                <div className="bg-secondary p-3 rounded-md">
                  <p className="text-sm text-muted-foreground">
                    <strong>Admin users</strong> have access to all stores automatically.
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="create-user-submit">
                  Create User
                </Button>
                <Button type="button" variant="secondary" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="users-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary border-b border-border">
              <tr>
                <th className="text-left p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Name</th>
                <th className="text-left p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Email</th>
                <th className="text-center p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Role</th>
                <th className="text-left p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Store Access</th>
                <th className="text-left p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Created</th>
                <th className="text-center p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center p-8 text-muted-foreground font-mono">Loading...</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center p-8 text-muted-foreground font-body">No users found</td>
                </tr>
              ) : (
                users.map((user) => {
                  const userStores = user.assigned_stores || [];
                  const storeDisplay = user.role === 'admin' 
                    ? 'All Stores' 
                    : userStores.length > 0 
                      ? userStores.map(sid => getStoreName(sid)).join(', ')
                      : 'No stores assigned';
                  
                  return (
                    <tr key={user.id} className="border-b border-border hover:bg-secondary/50 transition-colors" data-testid={`user-row-${user.email}`}>
                      <td className="p-4 font-body font-medium">{user.name}</td>
                      <td className="p-4 font-mono text-sm">{user.email}</td>
                      <td className="p-4">
                        <div className="flex justify-center">
                          <select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                            disabled={user.id === currentUser.id}
                            className={`px-3 py-1 rounded-md border font-mono text-xs uppercase tracking-widest ${getRoleBadgeColor(user.role)}`}
                            data-testid={`role-select-${user.email}`}
                          >
                            <option value="admin">Admin</option>
                            <option value="manager">Manager</option>
                            <option value="staff">Staff</option>
                          </select>
                        </div>
                      </td>
                      <td className="p-4 font-body text-sm">{storeDisplay}</td>
                      <td className="p-4 font-mono text-sm text-muted-foreground">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openEditStoresDialog(user)}
                            disabled={user.id === currentUser.id || user.role === 'admin'}
                            className="p-2 hover:bg-secondary rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Edit Store Access"
                            data-testid={`edit-stores-${user.email}`}
                          >
                            <Pencil size={16} strokeWidth={1.5} />
                          </button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            disabled={user.id === currentUser.id}
                            className="p-2 hover:bg-destructive/10 text-destructive rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid={`delete-user-${user.email}`}
                          >
                            <Trash2 size={16} strokeWidth={1.5} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Store Access Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-2xl">
              Edit Store Access - {editingUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">Select which stores this user can access:</p>
            <div className="space-y-2">
              {stores.map((store) => (
                <label key={store.id} className="flex items-center gap-3 p-3 bg-secondary rounded-md border border-border hover:bg-secondary/70 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedStores.includes(store.id)}
                    onChange={() => toggleStore(store.id)}
                    className="w-4 h-4"
                  />
                  <div className="flex-1">
                    <p className="font-body font-medium">{store.name}</p>
                    {store.address && (
                      <p className="text-xs text-muted-foreground">{store.address}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSaveStores} className="flex-1 bg-primary hover:bg-primary/90">
                Save Changes
              </Button>
              <Button variant="secondary" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
